import { useState, useEffect, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { FieldLabelRow } from './field-label-row';
import { LockToggle } from './-components/lock-toggle';
import { CharacterCount, type RecommendedLength } from './character-count';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';

export default function LockableTextField({
	label,
	description,
	optional,
	info,
	recommendedLength,
	sourceValue,
	...props
}: ComponentProps<'input'> & {
	label?: string;
	description?: ReactNode;
	optional?: boolean;
	info?: ReactNode;
	recommendedLength?: RecommendedLength;
	/**
	 * Optional value this field should stay in sync with while locked (e.g. an auto-generated
	 * source). When omitted, the lock only protects the existing value from accidental edits.
	 */
	sourceValue?: string;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);

	const value = field.state.value ?? '';
	const hasSource = sourceValue !== undefined;

	// Sync with sourceValue only while the field started empty. Once the field has
	// a persisted value, the lock becomes pure edit protection.
	const [syncEnabled] = useState(() => hasSource && !value);
	const [locked, setLocked] = useState(true);

	useEffect(() => {
		if (syncEnabled && locked && sourceValue !== value) {
			field.handleChange(sourceValue as string);
		}
		// `field` is a stable handle; we intentionally react to locked/sourceValue/value.
	}, [locked, sourceValue, value, syncEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (syncEnabled && !locked && value === sourceValue && sourceValue !== '') {
			setLocked(true);
		}
	}, [value, sourceValue, locked, syncEnabled]);

	const toggleLock = () => {
		const newLocked = !locked;
		setLocked(newLocked);
		if (syncEnabled && newLocked) {
			field.handleChange(sourceValue as string);
		}
	};

	return (
		<div>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
					/>
				)}
				<div
					className={cn(
						'flex h-8 items-center rounded-lg border bg-transparent text-base transition-[color,border-color,padding,box-shadow] md:text-sm',
						isReadOnly
							? 'border-transparent px-0'
							: locked
								? 'border-input/30 bg-muted/50 px-2.5'
								: 'border-input focus-within:border-ring px-2.5',
					)}
				>
					<input
						id={field.name}
						name={field.name}
						aria-invalid={hasFieldError(field.state.meta)}
						value={isReadOnly && !value ? '-' : value}
						onBlur={field.handleBlur}
						onChange={(e) => field.handleChange(e.target.value)}
						{...props}
						readOnly={isReadOnly || locked}
						tabIndex={isReadOnly ? -1 : undefined}
						className={cn(
							'min-w-0 flex-1 bg-transparent outline-none',
							locked && 'text-muted-foreground cursor-default',
						)}
					/>
					{!isReadOnly && <LockToggle locked={locked} onToggle={toggleLock} />}
				</div>
				{recommendedLength && <CharacterCount value={value} recommended={recommendedLength} />}
				{description && <p className="text-muted-foreground m-2 text-xs">{description}</p>}
				<FieldInfo fieldState={field.state} />
			</div>
		</div>
	);
}
